// toast.tsx
import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, CheckCircle, AlertCircle, X } from 'lucide-react';

const ToastContext = createContext(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({
  children,
}) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback(
    (messageOrOptions, type) => {
      const id = Date.now() + Math.random();

      // Handle both old and new formats
      const toastData = typeof messageOrOptions === 'string'
        ? { message: messageOrOptions, type: type || 'success' }
        : messageOrOptions;

      let newToasts = [...toasts, { id, ...toastData }];

      // Enhanced duration logic
      const duration = typeof messageOrOptions === 'object' && messageOrOptions.duration
        ? messageOrOptions.duration
        : toastData.type === 'warning'
          ? 6000
          : toastData.type === 'error'
            ? 'permanent'
            : 1500;

      if (duration !== 'permanent') {
        setTimeout(() => {
          setToasts(prev => prev.filter(toast => toast.id !== id));
        }, duration);
      }

      if (toasts.length >= 3) {
        newToasts = newToasts.slice(1);
      }

      setToasts(newToasts);
    },
    [toasts]
  );

  // Existing styles...
  const toastContainerStyle = {
    position: 'fixed',
    top: '106px',
    right: '16px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '384px',
    pointerEvents: 'none',
  };

  const getToastStyles = (type) => {
    const styles = {
      success: {
        bg: '#ECFDF5',
        border: '#D1FAE5',
        text: '#065F46',
        icon: '#059669',
      },
      error: {
        bg: '#FEF2F2',
        border: '#FEE2E2',
        text: '#991B1B',
        icon: '#DC2626',
      },
      warning: {
        bg: '#FFFBEB',
        border: '#FEF3C7',
        text: '#92400E',
        icon: '#D97706',
      },
    };

    return {
      display: 'flex',
      alignItems: 'flex-start', // Changed to align-items: flex-start for multi-line content
      padding: '16px',
      borderRadius: '8px',
      boxShadow:
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      backgroundColor: styles[type].bg,
      border: `1px solid ${styles[type].border}`,
      pointerEvents: 'auto',
      marginBottom: '8px',
    };
  };

  // Existing icon styles...
  const getIconStyles = type => ({
    width: '24px',
    height: '24px',
    marginRight: '12px',
    marginTop: '2px', // Added to align with first line of text
    flexShrink: 0,
    color: type === 'success'
      ? '#059669'
      : type === 'warning'
        ? '#D97706'
        : '#DC2626',
  });

  // Enhanced message container styles
  const getMessageContainerStyles = () => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  });

  const getTitleStyles = type => ({
    fontSize: '14px',
    fontWeight: 600,
    color: type === 'success'
      ? '#065F46'
      : type === 'warning'
        ? '#92400E'
        : '#991B1B',
  });

  const getMessageStyles = type => ({
    fontSize: '14px',
    fontWeight: 500,
    color: type === 'success'
      ? '#065F46'
      : type === 'warning'
        ? '#92400E'
        : '#991B1B',
  });

  const getDetailsStyles = type => ({
    fontSize: '12px',
    fontWeight: 400,
    color: type === 'success'
      ? '#065F46'
      : type === 'warning'
        ? '#92400E'
        : '#991B1B',
    opacity: 0.8,
  });

  const getListStyles = (type) => ({
    listStyleType: 'disc',
    paddingLeft: '15px',
    margin: 0,
    fontWeight: 400,
    fontSize: '12px',
    color: type === 'success'
      ? '#065F46'
      : type === 'warning'
        ? '#92400E'
        : '#991B1B',
  });

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div style={toastContainerStyle}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={getToastStyles(toast.type)}
            role="alert"
            aria-live="polite"
          >
            {toast.type === 'success' ? (
              <CheckCircle style={getIconStyles('success')} />
            ) : toast.type === 'warning' ? (
              <AlertCircle style={getIconStyles('warning')} />
            ) : (
              <AlertTriangle style={getIconStyles('error')} />
            )}
            <div style={getMessageContainerStyles()}>
              {toast.title && (
                <span style={getTitleStyles(toast.type)}>{toast.title}</span>
              )}
              <span style={getMessageStyles(toast.type)}>{toast.message}</span>
              {toast.details && (
                <span style={getDetailsStyles(toast.type)}>
                  {toast.details}
                </span>
              )}
              {toast.items && (
                <ul style={getListStyles(toast.type)}>
                  {toast.items.map((item) => <li>{item}</li>)}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                marginLeft: '16px',
                padding: '4px',
                borderRadius: '4px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Close notification"
            >
              <X
                size={16}
                color={toast.type === 'success'
                  ? '#059669'
                  : toast.type === 'warning'
                    ? '#D97706'
                    : '#DC2626'}
              />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
